import { Check, MagicWand, X } from "@phosphor-icons/react";
import { type NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import { useEffect, useState } from "react";
import TextareaAutosize from "react-textarea-autosize";

import { Button } from "common/components/ui/Button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "common/components/ui/Popover";
import { cn } from "common/helpers/utils";

export default function AiVariable({
  node,
  updateAttributes,
  editor,
  deleteNode,
}: NodeViewProps) {
  const { description } = node.attrs;

  const [textAreaValue, setTextAreaValue] = useState(description);
  const [isOpen, setIsOpen] = useState(!description);
  const [isEditable, setIsEditable] = useState(editor.isEditable);

  function onSave() {
    updateAttributes({
      description: textAreaValue,
    });
    setIsOpen(false);
  }

  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setTextAreaValue(description || "");
        if (!description) {
          if (editor.can().undo()) {
            editor.commands.undo();
          } else {
            deleteNode();
          }
        }
      }, 300);
    }
  }, [isOpen]);

  useEffect(() => {
    function onUpdate() {
      setIsEditable(editor.isEditable);
    }

    editor.on("update", onUpdate);

    return () => {
      // Clean up function
      editor.off("update", onUpdate);
    };
  }, [editor]);

  return (
    <NodeViewWrapper className="inline-flex">
      <Popover open={isOpen} onOpenChange={setIsOpen} modal>
        <PopoverTrigger
          disabled={!isEditable}
          className={cn(
            "text-purple-700 bg-lightPurpleAi inline-flex h-5 shrink-0 items-center justify-center gap-0.5 text-nowrap rounded-md px-0.5 text-button-sm",

            isOpen && "bg-lightPurple",
            isEditable && "hover:bg-lightPurple",
          )}
        >
          <MagicWand size={16} />
          <span className="max-w-32 truncate">
            {description || "Enter prompt"}
          </span>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="z-aboveDialog flex w-80 justify-between gap-x-2 rounded-3xl px-3 py-2 has-[:focus]:ring-2 has-[:focus]:ring-purple-400 lg:w-[540px]"
        >
          <MagicWand size={20} className="mt-1.5" />

          <TextareaAutosize
            id="aiDescriptor"
            maxRows={6}
            placeholder="Ask ai to generate"
            className="flex-1 resize-none self-center outline-none scrollbar-thin"
            defaultValue={description}
            value={textAreaValue}
            onChange={(e) => setTextAreaValue(e.target.value)}
          />

          <div className="flex items-end gap-x-1">
            <Button
              variant="outline-black"
              intent="iconOnly"
              size="sm"
              onClick={() => setIsOpen(false)}
            >
              <X />
            </Button>
            <Button
              variant="default-purple"
              intent="iconOnly"
              size="sm"
              onClick={onSave}
              disabled={!textAreaValue}
            >
              <Check />
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </NodeViewWrapper>
  );
}
